/**
 * Concrete wiring for the ENG-4321 terminal-outcome counting of the retry-prone
 * stream-recovery flow. Keeps analytics/domains/terminal-outcome.ts PURE (no
 * analytics-stack imports) by owning the one impure piece — the send — here.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — the pure decision +
 * controller is one primitive; this module is the emit wrapper that maps a
 * terminal decision onto the canonical event and ships it through the shared,
 * envelope-enriching track() path, plus the per-camera stall episode that keeps
 * several views of one camera from counting one recovery twice.
 */

import { track } from '#~/analytics/track.js';
import {
  EVENT_CATEGORIES,
  EVENT_NAMES,
  COMMON_PROPERTIES,
  DEVICE_PROPERTIES
} from '#~/analytics/constants/index.js';
import type { AnalyticsEvent } from '#~/analytics/types/events.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import {
  TERMINAL_WINDOW_MS,
  createTerminalOutcomeController,
  type AttemptOutcome,
  type StreamRecoveryContext,
  type TerminalOutcomeController,
  type TerminalOutcomeDecision
} from './terminal-outcome.js';

/**
 * Counting rule stamped on every recovery row: one row per observed retry
 * window that ended, with overlapping views and immediate remounts merged.
 */
export const RECOVERY_COUNT_SCOPE = 'observed_window';

/**
 * Ship one terminal stream-recovery outcome to analytics. Maps the decision's
 * result onto the canonical success/failure event and carries the
 * attempt_number / elapsed_ms / is_terminal props. Envelope enrichment
 * (launch_id / session_id / environment / app_version / client) is attached by
 * track() at send time, not here. This is the raw send: every call
 * ships. Merging the views of one camera happens in streamRecoveryController.
 */
export const emitStreamRecoveryOutcome = (
  decision: TerminalOutcomeDecision,
  context?: StreamRecoveryContext
): void => {
  const properties: EventProperties = {
    [COMMON_PROPERTIES.ATTEMPT_NUMBER]: decision.attemptNumber,
    [COMMON_PROPERTIES.ELAPSED_MS]: decision.elapsedMs,
    [COMMON_PROPERTIES.IS_TERMINAL]: decision.isTerminal,
    [COMMON_PROPERTIES.RESULT]: decision.result,
    // Which counting rule produced this row. Rows without it come from builds
    // that counted differently (per view, or per outage until playback) and
    // stay separately queryable.
    recovery_count_scope: RECOVERY_COUNT_SCOPE,
    // Without device_id, recovery rows cannot collapse to a camera; production
    // rows carried none. Stamp the bag the video host passed into beginWindow.
    ...(context?.device_id !== undefined && context.device_id !== ''
      ? { [DEVICE_PROPERTIES.DEVICE_ID_CANONICAL]: context.device_id }
      : {}),
    ...(context?.agent_id !== undefined && context.agent_id !== ''
      ? { agent_id: context.agent_id }
      : {}),
    ...(typeof context?.is_embedded === 'boolean' ? { is_embedded: context.is_embedded } : {})
  };
  // Typed literal per branch (no `as`): each object is a valid StreamRecoveryEvent
  // (category BRIDGE), so it is assignable to AnalyticsEvent by construction.
  const event: AnalyticsEvent =
    decision.result === 'success'
      ? {
          name: EVENT_NAMES.STREAM_RECOVERY_SUCCEEDED,
          category: EVENT_CATEGORIES.BRIDGE,
          properties
        }
      : { name: EVENT_NAMES.STREAM_RECOVERY_FAILED, category: EVENT_CATEGORIES.BRIDGE, properties };
  // track() swallows its own errors; void the promise so a send can never reject
  // into the caller's render/timer path.
  void track(event);
};

/** A terminal decision together with the context of the window that produced it. */
type TerminalOutcome = {
  decision: TerminalOutcomeDecision;
  context: StreamRecoveryContext | undefined;
};

/**
 * One stall of one camera, shared by every video view that shows it.
 *
 * WHY: a camera can be on screen in several views at once (tray tile and
 * stage). When its stream stalls, each view opens its own retry window, and
 * when the stream comes back each view closes its window milliseconds apart.
 * That is one recovery, so an episode reports at most one result. A success
 * from any view wins. A view that gives up while another view of the stall is
 * still retrying is held back, because the other view may yet recover; it is
 * sent only once no view of the stall is left open.
 */
type RecoveryEpisode = {
  /** Immediate remounts after failure share an observed window, without retaining a chain of old episodes. */
  windowIdentity: symbol;
  openKeys: Set<string>;
  reported: boolean;
  deferredFailure: TerminalOutcome | undefined;
  /**
   * The episode began while the camera's last reported failure was still
   * retained (a remount or new view straight after that failure, with no
   * playback in between), so its give-up belongs to that reported window.
   */
  continuesReportedFailure: boolean;
};

/** A view's playback belongs to the episode it joined, even after that window closes. */
export type StreamProgressOrigin = Readonly<{ deviceId: string; episode: RecoveryEpisode }>;

/** The episode a new window of each camera joins; replaced once it has reported. */
const currentEpisodeByDevice = new Map<string, RecoveryEpisode>();
/** The camera and episode each open window joined, so its result is routed there. */
const membershipByWindowKey = new Map<string, { deviceId: string; episode: RecoveryEpisode }>();
/**
 * Cameras whose last observed retry window ended in a reported failure, with
 * when that happened and the host's retry window (`windowMs`) for it.
 *
 * WHY: the event counts observed retry windows that ran out without playback
 * (ENG-4619), not physical outages. A view that remounts or opens straight
 * after a reported failure, with no playback in between, is the same observed
 * window and must not send a second failure. A window that starts later than
 * one host window after that failure is a new observed window and counts, even
 * if the camera stayed down the whole time; nothing here claims the camera
 * recovered while no view watched it. The retention reuses the host's own
 * window length and never changes how long or how often a view retries.
 * Playback belonging to this observed window, or a success, clears the entry at once.
 */
const reportedFailureByDevice = new Map<
  string,
  { at: number; windowMs: number; episode: RecoveryEpisode }
>();

/** The host retry window each open window was begun with, for retention. */
const windowMsByKey = new Map<string, number>();

/** Same clock the terminal-outcome controller uses for elapsed time. */
const now = (): number => Date.now();

const isRetainedFailure = (deviceId: string): boolean => {
  const reported = reportedFailureByDevice.get(deviceId);
  if (reported === undefined) {
    return false;
  }
  if (now() - reported.at <= reported.windowMs) {
    return true;
  }
  reportedFailureByDevice.delete(deviceId);
  return false;
};

const hasDeviceId = (deviceId: string | undefined): deviceId is string =>
  deviceId !== undefined && deviceId !== '';

const finalizeFailure = (
  deviceId: string,
  episode: RecoveryEpisode,
  failure: TerminalOutcome,
  windowMs: number
): void => {
  episode.reported = true;
  episode.deferredFailure = undefined;
  // Measured from this window's end either way: a continued window that also
  // ran out moves the retention forward, because views were watching the
  // camera without playback the whole time.
  reportedFailureByDevice.set(deviceId, { at: now(), windowMs, episode });
  if (episode.continuesReportedFailure) {
    return;
  }
  emitStreamRecoveryOutcome(failure.decision, failure.context);
};

/** Drops the camera's current episode once no window is open in it, so episodes never accumulate. */
const releaseIfIdle = (deviceId: string, episode: RecoveryEpisode): void => {
  if (episode.openKeys.size === 0 && currentEpisodeByDevice.get(deviceId) === episode) {
    currentEpisodeByDevice.delete(deviceId);
  }
};

const joinEpisode = (
  key: string,
  deviceId: string | undefined
): StreamProgressOrigin | undefined => {
  // Without a camera id the views cannot be proven to share a camera, so the
  // window stays outside any episode and its outcome is always sent.
  if (!hasDeviceId(deviceId)) {
    return;
  }
  let episode = currentEpisodeByDevice.get(deviceId);
  // A stall that starts after its episode reported is a new stall, however
  // soon it follows, so a flapping stream counts every recovery.
  if (episode === undefined || episode.reported) {
    const continuesReportedFailure = isRetainedFailure(deviceId);
    const retained = reportedFailureByDevice.get(deviceId);
    episode = {
      windowIdentity:
        continuesReportedFailure && retained !== undefined
          ? retained.episode.windowIdentity
          : Symbol(),
      openKeys: new Set(),
      reported: false,
      deferredFailure: undefined,
      continuesReportedFailure
    };
    currentEpisodeByDevice.set(deviceId, episode);
  }
  episode.openKeys.add(key);
  membershipByWindowKey.set(key, { deviceId, episode });
  return { deviceId, episode };
};

/** A window closed without a result (unmount, hidden tab, re-opened key). */
const leaveEpisode = (key: string): void => {
  const windowMs = windowMsByKey.get(key) ?? TERMINAL_WINDOW_MS;
  windowMsByKey.delete(key);
  const membership = membershipByWindowKey.get(key);
  if (membership === undefined) {
    return;
  }
  membershipByWindowKey.delete(key);
  const { deviceId, episode } = membership;
  episode.openKeys.delete(key);
  // The last view that could still have recovered is gone, so a held-back
  // give-up is now the stall's result.
  if (episode.openKeys.size === 0 && !episode.reported && episode.deferredFailure !== undefined) {
    finalizeFailure(deviceId, episode, episode.deferredFailure, windowMs);
  }
  releaseIfIdle(deviceId, episode);
};

/** Decides whether a window's terminal result is sent, held back or dropped. */
const routeTerminal = (key: string, terminal: TerminalOutcome): void => {
  const windowMs = windowMsByKey.get(key) ?? TERMINAL_WINDOW_MS;
  windowMsByKey.delete(key);
  const membership = membershipByWindowKey.get(key);
  if (membership === undefined) {
    emitStreamRecoveryOutcome(terminal.decision, terminal.context);
    return;
  }
  membershipByWindowKey.delete(key);
  const { deviceId, episode } = membership;
  episode.openKeys.delete(key);
  // A result that arrives after another view already reported this stall is dropped.
  if (!episode.reported) {
    if (terminal.decision.result === 'success') {
      episode.reported = true;
      episode.deferredFailure = undefined;
      reportedFailureByDevice.delete(deviceId);
      emitStreamRecoveryOutcome(terminal.decision, terminal.context);
    } else if (episode.openKeys.size > 0) {
      // Another view is still retrying and may recover; keep the earliest give-up.
      episode.deferredFailure ??= terminal;
    } else {
      finalizeFailure(deviceId, episode, episode.deferredFailure ?? terminal, windowMs);
    }
  }
  releaseIfIdle(deviceId, episode);
};

/**
 * Observed playback of a camera ends its reported window, so its next give-up
 * is a new failure at once. Called from the video `timeupdate` listener, so it
 * stays O(1).
 */
export const noteStreamProgress = (
  deviceId: string | undefined,
  origin?: StreamProgressOrigin
): void => {
  if (hasDeviceId(deviceId)) {
    if (origin !== undefined && origin.deviceId !== deviceId) return;
    const retainedFailure = reportedFailureByDevice.get(deviceId);
    // Per rule: regression-tests-for-state-machine-primitives (global). An
    // older view cannot clear a newer failure's retention merely by camera id.
    if (
      origin === undefined ||
      retainedFailure?.episode.windowIdentity === origin.episode.windowIdentity
    ) {
      reportedFailureByDevice.delete(deviceId);
    }
    // Playback ends the windows sharing this view's observed window, including
    // remounts continuing its retained failure. Their later give-up is not a
    // failure; a newer observed window of another view keeps its own outcome.
    // The viewer records its own recovery success before calling this, and
    // retains the origin from beginWindow. Its late give-up, success
    // or abandon is dropped and sets no retention, and the next stall starts a
    // fresh episode. Members keep pointing at the retired episode, and
    // releaseIfIdle checks the current episode's identity, so their cleanup
    // cannot touch the fresh one.
    // Origin-free ordinary playback still retires the current episode. A view
    // that joined an older observed window never retires a newer one.
    const current = currentEpisodeByDevice.get(deviceId);
    const episode =
      origin === undefined || current?.windowIdentity === origin.episode.windowIdentity
        ? current
        : origin.episode;
    if (episode !== undefined && !episode.reported) {
      episode.reported = true;
      episode.deferredFailure = undefined;
      if (currentEpisodeByDevice.get(deviceId) === episode) currentEpisodeByDevice.delete(deviceId);
    }
  }
};

// The generic controller's emit has no window key, so the terminal outcome is
// parked here and routed by the wrapper's recordAttempt, which is synchronous.
let capturedTerminal: TerminalOutcome | undefined;

/** Returns and clears the parked terminal outcome. */
const takeCapturedTerminal = (): TerminalOutcome | undefined => {
  const terminal = capturedTerminal;
  capturedTerminal = undefined;
  return terminal;
};

const recoveryWindows = createTerminalOutcomeController((decision, context) => {
  capturedTerminal = { decision, context };
});

/**
 * Process-wide terminal-outcome controller for stream recovery, keyed per reporting
 * window. A singleton so a re-rendered/re-mounted emit site shares the same
 * per-key window state and the at-most-once guard holds across renders. Each
 * window joins its camera's stall episode while open, and the episode decides
 * which of its views' results is sent.
 */
export const streamRecoveryController: Omit<TerminalOutcomeController, 'beginWindow'> & {
  beginWindow(
    ...args: Parameters<TerminalOutcomeController['beginWindow']>
  ): StreamProgressOrigin | undefined;
} = {
  beginWindow(key, budget, context) {
    // A re-opened key starts a fresh window, so it leaves its old episode first.
    leaveEpisode(key);
    const origin = joinEpisode(key, context?.device_id);
    windowMsByKey.set(key, budget?.windowMs ?? TERMINAL_WINDOW_MS);
    recoveryWindows.beginWindow(key, budget, context);
    return origin;
  },
  recordAttempt(key, outcome) {
    takeCapturedTerminal();
    const decision = recoveryWindows.recordAttempt(key, outcome);
    const terminal = takeCapturedTerminal();
    if (terminal !== undefined) {
      routeTerminal(key, terminal);
    }
    return decision;
  },
  abandonWindow(key) {
    recoveryWindows.abandonWindow(key);
    leaveEpisode(key);
  },
  hasOpenWindow(key) {
    return recoveryWindows.hasOpenWindow(key);
  }
};

/** An exact observed window; child callbacks must keep this identity rather than look up a camera. */
export type StreamRecoveryWindow = Readonly<{
  owner: symbol;
  key: string;
  origin: StreamProgressOrigin | undefined;
  hostDeadline: number;
}>;

let ownerWindowOrdinal = 0;

/**
 * One logical view survives its inner media replacement. Its detached window
 * expires as unknown; actual attempts still use the unchanged decision clock.
 * Per rule: think-in-systems (global), keep media recovery outside reporting.
 */
export const createStreamRecoveryOwner = (context: StreamRecoveryContext) => {
  const ownerIdentity = Symbol();
  const capturedContext = { ...context };
  let pending: StreamRecoveryWindow | undefined;
  let progressOrigin: StreamProgressOrigin | undefined;
  let activeAttachment: symbol | undefined;
  let gapTimeout: ReturnType<typeof setTimeout> | undefined;

  const clearGapTimeout = (): void => {
    if (gapTimeout !== undefined) clearTimeout(gapTimeout);
    gapTimeout = undefined;
  };

  const cancelWindow = (): void => {
    clearGapTimeout();
    const window = pending;
    pending = undefined;
    if (window !== undefined) streamRecoveryController.abandonWindow(window.key);
  };

  return {
    /** A new child can inherit only this owner's exact pending window. */
    attach() {
      // A throttled timer cannot extend a detached window beyond the original
      // host budget. This check never expires an attached confirmed attempt.
      if (
        activeAttachment === undefined &&
        pending !== undefined &&
        now() >= pending.hostDeadline
      ) {
        cancelWindow();
      }
      clearGapTimeout();
      const attachment = Symbol();
      activeAttachment = attachment;
      let attachmentOrigin = progressOrigin;
      return {
        pendingWindow: (): StreamRecoveryWindow | undefined =>
          activeAttachment === attachment ? pending : undefined,
        beginWindow(
          budget: Parameters<TerminalOutcomeController['beginWindow']>[1],
          hostStartedAt: number = now()
        ) {
          if (activeAttachment !== attachment) return;
          if (pending === undefined) {
            const key = `logical-video-recovery-${++ownerWindowOrdinal}`;
            progressOrigin = streamRecoveryController.beginWindow(key, budget, capturedContext);
            pending = {
              owner: ownerIdentity,
              key,
              origin: progressOrigin,
              hostDeadline: hostStartedAt + (budget?.windowMs ?? TERMINAL_WINDOW_MS)
            };
          }
          attachmentOrigin = pending.origin;
          return pending;
        },
        recordAttempt(window: StreamRecoveryWindow | undefined, outcome: AttemptOutcome) {
          // Per rule: regression-tests-for-state-machine-primitives (global).
          // A disposed child or an old token must never settle a newer window.
          if (activeAttachment !== attachment || window === undefined || pending !== window) return;
          const decision = streamRecoveryController.recordAttempt(window.key, outcome);
          if (decision?.isTerminal === true) {
            pending = undefined;
            clearGapTimeout();
          }
          return decision;
        },
        noteProgress(window: StreamRecoveryWindow | undefined): void {
          if (activeAttachment !== attachment) return;
          if (window !== undefined && window.owner !== ownerIdentity) return;
          if (window !== undefined && pending === window) {
            const decision = streamRecoveryController.recordAttempt(window.key, 'success');
            if (decision?.isTerminal === true) {
              pending = undefined;
              clearGapTimeout();
            }
          }
          // Success must precede playback retirement. Retain the origin even
          // after cancellation/settlement so old playback cannot erase a newer view.
          noteStreamProgress(capturedContext.device_id, window?.origin ?? attachmentOrigin);
        },
        cancel(): void {
          if (activeAttachment === attachment) cancelWindow();
        },
        detach(): void {
          if (activeAttachment !== attachment) return;
          activeAttachment = undefined;
          const window = pending;
          if (window === undefined) return;
          const remaining = window.hostDeadline - now();
          if (remaining <= 0) {
            cancelWindow();
            return;
          }
          // No child can observe an outcome during this gap. Silence stays
          // unknown; only the original host deadline bounds retained ownership.
          gapTimeout = setTimeout(() => {
            gapTimeout = undefined;
            if (activeAttachment === undefined && pending === window) cancelWindow();
          }, remaining);
        }
      };
    },
    /** Hidden views cancel unknown without invalidating their still-mounted child. */
    cancel(): void {
      cancelWindow();
    },
    /** Disposal/identity replacement releases this view alone, without inventing an outcome. */
    dispose(): void {
      activeAttachment = undefined;
      cancelWindow();
    }
  };
};

export type StreamRecoveryOwner = ReturnType<typeof createStreamRecoveryOwner>;
export type StreamRecoveryAttachment = ReturnType<StreamRecoveryOwner['attach']>;
